import React, { useState, useEffect, useMemo } from 'react'
import { X, UserPlus, Send } from 'lucide-react'
import { Button } from '../ui'
import { userService, InviteUserRequest } from '../../services/userService'
import { authService, Role } from '../../services/authService'
import { organizationService, Organization } from '../../services/organizationService'
import { useAuthStore } from '../../stores/authStore'
import { getFilteredRolesForDisplay } from '../../utils/roleHelpers'
import { canAccessSupportPage } from '../../utils/permissions'
import { useToast } from '../../contexts/ToastContext'

interface InviteUserModalProps {
  isOpen: boolean
  onClose: () => void
  onInviteSent: () => void
  defaultOrganizationId?: string  // Pre-fill and hide org dropdown (e.g. from org detail context)
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  onInviteSent,
  defaultOrganizationId
}) => {
  const { user: currentUser } = useAuthStore()
  const { showSuccess, showError } = useToast()
  const [loading, setLoading] = useState(false)
  const [loadingRoles, setLoadingRoles] = useState(false)
  const [loadingOrganizations, setLoadingOrganizations] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [roles, setRoles] = useState<Role[]>([])
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [formData, setFormData] = useState({
    email: '',
    roleId: 1,
    organizationId: '',
    invitationMessage: ''
  })
  const [emailError, setEmailError] = useState<string>('')
  const [emailTouched, setEmailTouched] = useState(false)

  // Check if current user has support access
  const isSupportUser = useMemo(
    () => canAccessSupportPage(currentUser),
    [currentUser]
  )

  // Email validation regex - stricter domain validation
  const validateEmail = (email: string): boolean => {
    // More comprehensive email validation
    // - Local part: alphanumeric, dots, hyphens, underscores, plus signs
    // - Domain: must have at least 2 characters
    // - TLD: must have at least 2 characters (rejects single letter TLDs)
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

    if (!emailRegex.test(email)) {
      return false
    }

    // Additional checks
    const parts = email.split('@')
    if (parts.length !== 2) return false

    const [localPart, domainPart] = parts

    // Local part shouldn't start or end with a dot
    if (localPart.startsWith('.') || localPart.endsWith('.')) return false

    // Domain should have at least one dot and proper structure
    const domainParts = domainPart.split('.')
    if (domainParts.length < 2) return false

    // Each domain part should have at least 2 characters (except for rare cases)
    // and shouldn't start or end with hyphen
    for (const part of domainParts) {
      if (part.length < 1) return false
      if (part.startsWith('-') || part.endsWith('-')) return false
    }

    // TLD should be at least 2 characters
    const tld = domainParts[domainParts.length - 1]
    if (tld.length < 2) return false

    return true
  }

  // Filter roles based on current user's permissions
  const filteredRoles = useMemo(
    () => getFilteredRolesForDisplay(roles, currentUser),
    [roles, currentUser]
  )

  // Load roles when modal opens
  useEffect(() => {
    const loadRoles = async () => {
      if (isOpen) {
        setLoadingRoles(true)
        try {
          const response = await authService.getRoles()
          if (response.success) {
            setRoles(response.data)
          }
        } catch (error) {
          console.error('Error loading roles:', error)
        } finally {
          setLoadingRoles(false)
        }
      }
    }

    if (roles.length === 0) {
      loadRoles()
    }
  }, [isOpen])

  // Load organizations when modal opens (only for support users)
  useEffect(() => {
    const loadOrganizations = async () => {
      if (isOpen && isSupportUser) {
        setLoadingOrganizations(true)
        try {
          const response = await organizationService.getOrganizations({ limit: 1000, isActive: true })
          if (response.success) {
            setOrganizations(response.data.organizations)
          }
        } catch (error) {
          console.error('Error loading organizations:', error)
        } finally {
          setLoadingOrganizations(false)
        }
      }
    }

    if (organizations.length === 0 && isSupportUser) {
      loadOrganizations()
    }
  }, [isOpen, isSupportUser])

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        email: '',
        roleId: 1,
        organizationId: defaultOrganizationId || (isSupportUser ? '' : (currentUser?.organizationId || '')),
        invitationMessage: ''
      })
      setError(null)
      setEmailError('')
      setEmailTouched(false)
    }
  }, [isOpen, isSupportUser, currentUser, defaultOrganizationId])

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }))

    // Validate email in real-time
    if (field === 'email') {
      const email = value.trim()
      if (email === '') {
        setEmailError('')
      } else if (!validateEmail(email)) {
        setEmailError('Please enter a valid email address')
      } else {
        setEmailError('')
      }
    }
  }

  const handleEmailBlur = () => {
    setEmailTouched(true)
    if (formData.email.trim() && !validateEmail(formData.email.trim())) {
      setEmailError('Please enter a valid email address')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.email.trim()) {
      setError('Email address is required')
      return
    }

    if (!validateEmail(formData.email.trim())) {
      setEmailError('Please enter a valid email address')
      setEmailTouched(true)
      return
    }

    // Determine which organization to use
    const organizationId = isSupportUser
      ? formData.organizationId
      : currentUser?.organizationId

    if (!organizationId) {
      setError(isSupportUser ? 'Please select an organization' : 'No organization found for current user')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const inviteData: InviteUserRequest = {
        email: formData.email.trim(),
        roleId: formData.roleId,
        organizationId: organizationId,
        invitationMessage: formData.invitationMessage.trim() || undefined
      }

      const response = await userService.inviteUser(inviteData)

      if (response.success) {
        showSuccess(
          'Invitation Sent',
          `An invitation has been sent to ${formData.email.trim()}`
        )
        onInviteSent()
      } else {
        throw new Error(response.error?.message || 'Failed to send invitation')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred'
      setError(errorMessage)
      showError('Invitation Failed', errorMessage)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className='fixed inset-0 z-50 overflow-hidden'>
      <div
        className='absolute inset-0 bg-black bg-opacity-50'
        onClick={onClose}
      />

      <div className='absolute inset-0 flex items-center justify-center p-4'>
        <div className='bg-white rounded-lg shadow-xl max-w-lg w-full'>
          {/* Header */}
          <div className='flex items-center justify-between px-5 py-3 border-b border-gray-200'>
            <div className='flex items-center space-x-2.5'>
              <div className='p-1.5 bg-blue-100 rounded-lg'>
                <UserPlus className='h-4 w-4 text-blue-600' />
              </div>
              <div>
                <h2 className='text-sm font-semibold text-gray-900'>
                  Invite User
                </h2>
                <p className='text-xs text-gray-600'>
                  Send an invitation to join your organization
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className='p-1.5 hover:bg-gray-100 rounded-lg transition-colors'
            >
              <X className='h-4 w-4 text-gray-400' />
            </button>
          </div>

          {/* Content */}
          <div className='px-5 py-4'>
            <form onSubmit={handleSubmit} className='space-y-3.5'>
              {/* Error Message */}
              {error && (
                <div className='bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-xs'>
                  {error}
                </div>
              )}

              {/* Email */}
              <div>
                <label className='block text-xs font-medium text-gray-700 mb-1.5'>
                  Email Address *
                </label>
                <input
                  type='email'
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  onBlur={handleEmailBlur}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-xs ${
                    emailError && emailTouched
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-avi-primary focus:border-transparent'
                  }`}
                  placeholder='user@example.com'
                  required
                />
                {emailError && emailTouched && (
                  <p className='mt-1 text-xs text-red-600'>{emailError}</p>
                )}
              </div>

              {/* Organization (only for support users without a pre-filled org) */}
              {isSupportUser && !defaultOrganizationId && (
                <div>
                  <label className='block text-xs font-medium text-gray-700 mb-1.5'>
                    Organization *
                  </label>
                  <select
                    value={formData.organizationId}
                    onChange={(e) =>
                      handleInputChange('organizationId', e.target.value)
                    }
                    className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent text-xs'
                    disabled={loadingOrganizations}
                    required
                  >
                    <option value=''>Select an organization...</option>
                    {loadingOrganizations ? (
                      <option value='' disabled>Loading organizations...</option>
                    ) : organizations.length === 0 ? (
                      <option value='' disabled>No organizations available</option>
                    ) : (
                      organizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.organizationName}
                        </option>
                      ))
                    )}
                  </select>
                  <p className='text-xs text-gray-500 mt-1'>
                    Select which organization this user will belong to
                  </p>
                </div>
              )}

              {/* Role */}
              <div>
                <label className='block text-xs font-medium text-gray-700 mb-1.5'>
                  Role
                </label>
                <select
                  value={formData.roleId}
                  onChange={(e) =>
                    handleInputChange('roleId', parseInt(e.target.value))
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent text-xs'
                  disabled={loadingRoles}
                >
                  {loadingRoles ? (
                    <option value=''>Loading roles...</option>
                  ) : filteredRoles.length === 0 ? (
                    <option value=''>No roles available</option>
                  ) : (
                    filteredRoles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.displayName}
                      </option>
                    ))
                  )}
                </select>
                <p className='text-xs text-gray-500 mt-1'>
                  {
                    filteredRoles.find((r) => r.id === formData.roleId)
                      ?.roleDescription
                  }
                </p>
              </div>

              {/* Invitation Message */}
              <div>
                <label className='block text-xs font-medium text-gray-700 mb-1.5'>
                  Welcome Message (Optional)
                </label>
                <textarea
                  value={formData.invitationMessage}
                  onChange={(e) =>
                    handleInputChange('invitationMessage', e.target.value)
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent text-xs resize-none'
                  placeholder="Welcome to our team! We're excited to have you join us."
                  rows={3}
                />
                <p className='text-xs text-gray-500 mt-1'>
                  This message will be included in the invitation email
                </p>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className='flex items-center justify-end space-x-2 px-5 py-3 border-t border-gray-200 bg-gray-50'>
            <Button variant='outline' onClick={onClose} size='sm'>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                loading ||
                !formData.email.trim() ||
                !!emailError ||
                !validateEmail(formData.email.trim()) ||
                (isSupportUser && !formData.organizationId)
              }
              size='sm'
              className='min-w-[100px]'
            >
              {loading ? (
                'Sending...'
              ) : (
                <>
                  <Send className='h-3.5 w-3.5 mr-1.5' />
                  Send Invite
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InviteUserModal
